import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router";
import { useMutation } from "urql";
import { metaDefault } from "../../config/meta";
import PlanPerks from "./PlanPerks";
import {
  DEVICE_COUNT_CHAR_LIMIT,
  EMAIL_CHAR_LIMIT,
  getLangCode,
  recaptchaSiteKey,
  STUDENT_COUNT_CHAR_LIMIT,
  TEXT_FIELD_CHAR_LIMIT,
  userRoles,
} from "../../config";
import { routeCodes } from "../../config/routes";
import { useLazyQuery } from "../../hooks";
import { RECAPTCHA_VERIFY_QUERY } from "../../graphql/queries";
import { SCHOOL_RFQ_MUTATION } from "../../graphql/mutations";
import { formatPhoneNumber, getString, handlePostRequest } from "../../utilities";
import { emailReg, getMaxLengthValidator } from "../../utilities/forms/rules";
import { gtmGAEvent } from "../../utilities/gtm";
import { inferInlineValidationErrorType, mpFieldValidationFailed } from "../../utilities/mixpanel";
import LoadingAnim from "../global/loadingAnim";
import OKModal from "../global/okModal";
import Select from "../global/Select";
import ErrorMessage from "../global/errorMessage";
import TextViewHook from "../global/textViewHook";
import Testimonials from "./Testimonials";
import Faq from "./Faq";

export default function RequestQuote() {
  const action = "RFQ_Form_Submission";
  const location = useLocation();

  const [countriesArray, setCountriesArray] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState(null);
  const [successModal, setSuccessModal] = useState(false);

  const navigate = useNavigate();

  const {
    control,
    formState: { errors },
    getFieldState,
    handleSubmit,
    register,
    getValues,
    trigger,
    watch,
    setValue,
  } = useForm({ mode: "onChange" });

  const phoneValue = watch("phone");

  useEffect(() => {
    const loadCountries = async () => {
      const { default: countryData } = await import(`../../utilities/countries/${getLangCode()}`);
      setCountriesArray(countryData);
    };

    loadCountries();
  }, []);

  useEffect(() => {
    if (phoneValue) {
      const formattedValue = formatPhoneNumber(phoneValue);
      setValue("phone", formattedValue);
    }
  }, [phoneValue, setValue]);

  const onSendCompleted = () => {
    setSubmitting(false);
    setSuccessModal(true);
    setSubmitErrors(null);
  };

  const onSendError = (error) => {
    setSubmitErrors({ message: error.message });
    setSubmitting(false);
  };

  const [result, sendRfqEmail] = useMutation(SCHOOL_RFQ_MUTATION);
  const { fetching: emailLoading } = result;

  const getVariables = () => {
    const { name, email, phone, position, country, schoolName, schoolAddress, deviceCount, studentCount } = getValues();
    return {
      name,
      email,
      phone,
      position: position.label,
      country: country.name,
      schoolName,
      address: schoolAddress,
      ...(deviceCount.length > 0 && { deviceCount: parseInt(deviceCount) }),
      studentCount: parseInt(studentCount),
    };
  };

  const onRecaptchaCompleted = ({ reCaptchaVerify }) => {
    if (reCaptchaVerify) {
      if (reCaptchaVerify.success) {
        // verified
        const variables = getVariables();
        variables.verifyKey = reCaptchaVerify.key;
        variables.verifyValue = action;

        if (variables) {
          sendRfqEmail(variables).then((result) => handlePostRequest(result, onSendCompleted, onSendError));
        }
      } else {
        // not verified
        setSubmitting(false);
        setSubmitErrors({ message: reCaptchaVerify.message });
      }
    }
  };

  const onRecaptchaError = (error) => {
    setSubmitting(false);
    setSubmitErrors({ message: error.message });
  };

  const { executeQuery: recaptchaVerify, loading: recaptchaLoading } = useLazyQuery(
    RECAPTCHA_VERIFY_QUERY,
    onRecaptchaCompleted,
    onRecaptchaError,
  );

  const loading = recaptchaLoading || emailLoading;

  const trackFieldValidationFailed = ({ fieldName = "", errorMessageDisplayed = "" }) => {
    if (!fieldName || !errorMessageDisplayed) {
      return;
    }

    mpFieldValidationFailed({
      fieldName,
      errorType: inferInlineValidationErrorType(fieldName),
      errorMessageDisplayed,
      form: "quote_request",
    });
  };

  const handleFieldBlurValidation = async (fieldName) => {
    const isValid = await trigger(fieldName);
    if (!isValid) {
      const fieldError = getFieldState(fieldName).error;
      trackFieldValidationFailed({
        fieldName,
        errorMessageDisplayed: fieldError?.message || "",
      });
    }
  };

  return (
    <div className="subscribe-wrapper">
      {metaDefault({
        path: location.pathname,
        title: getString("subscribe.plans.school.requestQuote"),
      })}

      <div className="subscribe-form-wrapper request-quote">
        <div className="subscribe-container">
          <h1>{getString("subscribe.plans.school.requestQuote")?.toUpperCase()}</h1>

          <h2>{getString("subscribe.plans.school.rfqSubHeader")}</h2>

          <PlanPerks devices={getString("subscribe.plans.perks.devices.school")} />

          <FormProvider register={register} getValues={getValues}>
            <form
              className="subscribe-form"
              aria-label="form"
              autoComplete="off"
              onSubmit={handleSubmit(() => {
                // reset if errors are present
                if (submitErrors) {
                  setSubmitErrors(null);
                }

                // perform reCaptcha to get token
                window.grecaptcha.enterprise.ready(async () => {
                  const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action });

                  if (token) {
                    recaptchaVerify({ token, action, verify: true, siteKey: recaptchaSiteKey });
                  }
                });
              })}
            >
              <h3>{getString("forms.yourContact")}</h3>

              <TextViewHook
                name="name"
                errors={errors}
                placeholder={getString("forms.name.0")}
                label={getString("forms.name.0")}
                shouldLabelAnimate={false}
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", { replace: [getString("forms.name.0")] }),
                  maxLength: getMaxLengthValidator(getString("forms.name.0"), TEXT_FIELD_CHAR_LIMIT),
                  onBlur: async () => {
                    await handleFieldBlurValidation("name");
                  },
                }}
              />
              <TextViewHook
                name="email"
                errors={errors}
                placeholder={getString("forms.email.0")}
                label={getString("forms.email.0")}
                shouldLabelAnimate={false}
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString("forms.email.2")],
                  }),
                  maxLength: getMaxLengthValidator(getString("forms.email.2"), EMAIL_CHAR_LIMIT),
                  pattern: {
                    value: emailReg,
                    message: getString("forms.errorMessages.email.0", {
                      replace: [getString("forms.email.0")],
                    }),
                  },
                  onBlur: async () => {
                    await handleFieldBlurValidation("email");
                  },
                }}
              />
              <TextViewHook
                name="phone"
                errors={errors}
                type="tel"
                placeholder={getString("forms.phonePlaceholder")}
                label={getString("forms.phone.0")}
                shouldLabelAnimate={false}
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString("forms.phone.1")],
                  }),
                  onBlur: async () => {
                    await handleFieldBlurValidation("phone");
                  },
                }}
              />
              <Controller
                control={control}
                name="position"
                defaultValue=""
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString("forms.position.0")],
                  }),
                }}
                render={({ field: { onChange, onBlur }, fieldState }) => (
                  <Select
                    onChange={onChange}
                    onBlur={() => {
                      onBlur();
                      handleFieldBlurValidation("position");
                    }}
                    {...fieldState}
                    label={getString("forms.position.0")}
                    placeholder={getString("forms.positionPlaceholder")}
                    items={[
                      { label: getString("subscribe.rfq.positions.teacher") },
                      { label: getString("subscribe.rfq.positions.tech") },
                      { label: getString("subscribe.rfq.positions.principal") },
                      { label: getString("subscribe.rfq.positions.admin") },
                      { label: getString("subscribe.rfq.positions.librarian") },
                      { label: getString("subscribe.rfq.positions.math") },
                      { label: getString("subscribe.rfq.positions.leader") },
                      { label: getString("subscribe.rfq.positions.other") },
                    ]}
                    searchKey="label"
                    dataTestId={"rfq-position-option"}
                  />
                )}
              />

              <h3>{getString("forms.yourDistrictSchool")}</h3>

              <Controller
                control={control}
                name="country"
                defaultValue=""
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString("forms.country.0")],
                  }),
                }}
                render={({ field: { onChange, onBlur }, fieldState }) => (
                  <Select
                    onChange={onChange}
                    onBlur={() => {
                      onBlur();
                      handleFieldBlurValidation("country");
                    }}
                    {...fieldState}
                    label={getString("forms.country.0")}
                    placeholder={getString("forms.country.0")}
                    items={countriesArray}
                    searchKey="name"
                    dataTestId={"rfq-country-option"}
                  />
                )}
              />
              <TextViewHook
                name="schoolName"
                errors={errors}
                placeholder={getString("forms.schoolName.0")}
                label={getString("forms.schoolName.0")}
                shouldLabelAnimate={false}
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString("forms.schoolName.1")],
                  }),
                  maxLength: getMaxLengthValidator(getString("forms.schoolName.1"), TEXT_FIELD_CHAR_LIMIT),
                  onBlur: async () => {
                    await handleFieldBlurValidation("schoolName");
                  },
                }}
              />
              <TextViewHook
                name="schoolAddress"
                errors={errors}
                placeholder={getString("forms.address.0")}
                label={getString("forms.schoolAddress.0")}
                shouldLabelAnimate={false}
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString("forms.schoolAddress.1")],
                  }),
                  maxLength: getMaxLengthValidator(getString("forms.schoolAddress.1"), TEXT_FIELD_CHAR_LIMIT),
                  onBlur: async () => {
                    await handleFieldBlurValidation("schoolAddress");
                  },
                }}
              />
              <TextViewHook
                name="deviceCount"
                errors={errors}
                placeholder={getString("forms.deviceCount.0")}
                label={getString("forms.deviceCount.0")}
                shouldLabelAnimate={false}
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString("forms.deviceCount.1")],
                  }),
                  maxLength: getMaxLengthValidator(getString("forms.deviceCount.1"), DEVICE_COUNT_CHAR_LIMIT),
                  onBlur: async () => {
                    await handleFieldBlurValidation("deviceCount");
                  },
                }}
                type="number"
              />
              <TextViewHook
                name="studentCount"
                errors={errors}
                placeholder={getString("forms.studentCount.0")}
                label={getString("forms.studentCount.0")}
                shouldLabelAnimate={false}
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString("forms.studentCount.1")],
                  }),
                  maxLength: getMaxLengthValidator(getString("forms.studentCount.1"), STUDENT_COUNT_CHAR_LIMIT),
                  onBlur: async () => {
                    await handleFieldBlurValidation("studentCount");
                  },
                }}
                type="number"
              />

              {submitErrors && <ErrorMessage error={submitErrors} />}

              <button
                className="subscribe-submit button-flat-color pt-green round auto"
                type="submit"
                onClick={() => {
                  gtmGAEvent({
                    eventAction: "button-click",
                    eventCategory: "IXL/ABCya School Request for Quote",
                    eventLabel: location.pathname,
                  });
                }}
              >
                {getString("forms.getAQuote")}
              </button>
            </form>
          </FormProvider>
        </div>
      </div>

      <Testimonials userRole={userRoles.school} />

      <Faq userRole={userRoles.school} />

      <OKModal
        doShow={successModal}
        close={() => {
          navigate(routeCodes.HOME);
        }}
        title={getString("thankyou.1")}
        button={getString("subscribe.school.home")}
      >
        <p>{getString("subscribe.school.thankyou")}</p>
        <p>{getString("subscribe.school.respond")}</p>
      </OKModal>

      {(loading || submitting) && (
        <LoadingAnim position="fixed" className="background-transparent-pt-dark-space orange">
          <h4>{getString("send.1")}</h4>
        </LoadingAnim>
      )}
    </div>
  );
}
